import React, { useEffect, useState } from "react";
import { Target, Pencil, X, TrendingUp, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatDeShort } from "@/lib/date";
import GermanDateInput from "@/components/GermanDateInput";

const todayIso = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  title: "Vertrags-Ziel",
  target_ue: "",
  period_start: todayIso(),
  signed_by: "",
  start_by: "",
  payment_expected_by: "",
});

/** Team-weites Liquiditäts-Ziel-Widget: "X UE bis Datum unterschrieben, Start bis Datum,
 * damit Geld bis Datum da ist" — mit wöchentlicher Soll-Zahl. Sichtbar für alle, editierbar
 * nur für Admin/Manager. */
export default function CompanyGoalWidget() {
  const { user } = useAuth();
  const isAdmin = user?.is_master_admin || user?.role === "admin" || user?.role === "manager";
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/company-goal");
      setGoal(res.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = () => {
    setForm(goal ? {
      title: goal.title || "Team-Ziel",
      target_ue: goal.target_ue,
      period_start: goal.period_start,
      signed_by: goal.signed_by,
      start_by: goal.start_by,
      payment_expected_by: goal.payment_expected_by || "",
    } : emptyForm());
    setEditing(true);
  };

  const save = async () => {
    if (!form.target_ue || Number(form.target_ue) <= 0) { toast.error("Bitte Ziel-UE angeben"); return; }
    if (!form.signed_by) { toast.error("Bitte Vertrags-Frist angeben"); return; }
    if (!form.start_by) { toast.error("Bitte Start-Frist angeben"); return; }
    setSaving(true);
    try {
      const res = await api.post("/company-goal", {
        ...form,
        target_ue: Number(form.target_ue),
        payment_expected_by: form.payment_expected_by || null,
      });
      setGoal(res.data);
      setEditing(false);
      toast.success("Team-Ziel gespeichert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const clearGoal = async () => {
    if (!window.confirm("Team-Ziel wirklich entfernen?")) return;
    try {
      await api.delete("/company-goal");
      setGoal(null);
      toast.success("Team-Ziel entfernt");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return null;
  if (!goal && !isAdmin) return null;

  return (
    <div className="border border-slate-200 bg-white mb-8" data-testid="company-goal-widget">
      {!goal ? (
        <div className="p-6 flex items-center justify-between">
          <div className="text-sm text-slate-500">Noch kein Team-Ziel gesetzt.</div>
          <button
            data-testid="company-goal-create-btn"
            onClick={openEdit}
            className="text-xs font-bold uppercase tracking-overline px-3 py-2 bg-[#0A0A0A] text-white hover:bg-slate-800"
          >
            Ziel setzen
          </button>
        </div>
      ) : (
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Target size={16} strokeWidth={1.5} className="text-[#0a788c]" />
              <div>
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Vertrags-Ziel</div>
                <div className="font-heading text-xl font-light tracking-tighter text-slate-900" data-testid="company-goal-title">{goal.title}</div>
              </div>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <button onClick={openEdit} data-testid="company-goal-edit-btn" className="text-slate-400 hover:text-slate-700"><Pencil size={14} strokeWidth={1.5} /></button>
                <button onClick={clearGoal} data-testid="company-goal-delete-btn" className="text-slate-400 hover:text-red-600"><X size={14} strokeWidth={1.5} /></button>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-mono text-3xl font-light text-slate-900" data-testid="company-goal-progress-ue">{goal.progress_ue}</span>
            <span className="text-sm text-slate-500">/ {goal.target_ue} UE aus neuen Verträgen ({goal.progress_count} Kunden)</span>
          </div>
          <div className="mt-2 h-2 bg-slate-100 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-[#00CCC9]" style={{ width: `${Math.min(100, goal.progress_pct)}%` }} />
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div>
              <div className="text-[9px] font-bold uppercase tracking-overline text-slate-400">Verträge bis</div>
              <div className="font-mono text-slate-800">{formatDeShort(goal.signed_by)}</div>
            </div>
            <div>
              <div className="text-[9px] font-bold uppercase tracking-overline text-slate-400">Start spätestens</div>
              <div className="font-mono text-slate-800">{formatDeShort(goal.start_by)}</div>
            </div>
            {goal.payment_expected_by && (
              <div>
                <div className="text-[9px] font-bold uppercase tracking-overline text-slate-400">Geld erwartet bis</div>
                <div className="font-mono text-slate-800">{formatDeShort(goal.payment_expected_by)}</div>
              </div>
            )}
          </div>

          <div
            data-testid="company-goal-weekly-pace"
            className={`mt-4 p-3 flex items-center gap-2 text-sm ${goal.is_overdue ? "bg-red-50 text-red-800 border border-red-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`}
          >
            {goal.is_overdue ? <AlertTriangle size={16} strokeWidth={1.5} /> : <TrendingUp size={16} strokeWidth={1.5} />}
            {goal.is_overdue ? (
              <span><b>Frist überschritten</b> — noch {goal.remaining_ue} UE offen, seit {formatDeShort(goal.signed_by)} fällig.</span>
            ) : goal.remaining_ue <= 0 ? (
              <span><b>Ziel erreicht 🎉</b></span>
            ) : (
              <span>Noch <b>{goal.remaining_ue} UE</b> offen · <b>{goal.days_remaining} Tage</b> bis zur Frist → diese Woche werden noch <b>{goal.weekly_required_ue} UE</b> benötigt.</span>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="company-goal-dialog">
          <div className="bg-white w-full max-w-md border border-slate-200">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <span className="font-heading text-lg font-light tracking-tighter">Team-Ziel bearbeiten</span>
              <button onClick={() => setEditing(false)} data-testid="company-goal-dialog-close"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Titel</label>
                <input data-testid="company-goal-title-input" className="input-tech text-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Ziel-UE (aus neuen Verträgen)</label>
                <input data-testid="company-goal-target-ue-input" type="number" min="1" className="input-tech font-mono text-sm" value={form.target_ue} onChange={(e) => setForm({ ...form, target_ue: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Zählt ab (Vertrag angelegt ab)</label>
                <GermanDateInput data-testid="company-goal-period-start-input" className="w-full" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Verträge unterschrieben bis</label>
                <GermanDateInput data-testid="company-goal-signed-by-input" className="w-full" value={form.signed_by} onChange={(e) => setForm({ ...form, signed_by: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Kunden starten spätestens</label>
                <GermanDateInput data-testid="company-goal-start-by-input" className="w-full" value={form.start_by} onChange={(e) => setForm({ ...form, start_by: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Geld erwartet bis (optional)</label>
                <GermanDateInput data-testid="company-goal-payment-by-input" className="w-full" value={form.payment_expected_by} onChange={(e) => setForm({ ...form, payment_expected_by: e.target.value })} />
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setEditing(false)} className="text-xs font-bold uppercase tracking-overline px-3 py-2 text-slate-500 hover:text-slate-800">Abbrechen</button>
              <button
                data-testid="company-goal-save-btn"
                onClick={save} disabled={saving}
                className="text-xs font-bold uppercase tracking-overline px-4 py-2 bg-[#0A0A0A] text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? "Speichern…" : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
